export type VenueCategoryIconName =
  | "beer"
  | "bottleWine"
  | "building2"
  | "circle"
  | "flaskConical"
  | "heart"
  | "laugh"
  | "lock"
  | "mapPin"
  | "mic"
  | "music"
  | "music2"
  | "sofa"
  | "trophy"
  | "wind";

export type VenueCategoryVisualSpec = {
  bgColor: string;
  color: string;
  iconName: VenueCategoryIconName;
  label: string;
};

export const VENUE_CATEGORY_VISUALS = {
  music_venue: {
    label: "Music",
    iconName: "music",
    color: "EF4E2E",
    bgColor: "2A1208",
  },
  nightclub: {
    label: "Club",
    iconName: "music2",
    color: "A855F7",
    bgColor: "1A0A2A",
  },
  bar: {
    label: "Bar",
    iconName: "bottleWine",
    color: "3B82F6",
    bgColor: "0A1628",
  },
  dive_bar: {
    label: "Dive Bar",
    iconName: "beer",
    color: "3B82F6",
    bgColor: "0A1628",
  },
  sports_bar: {
    label: "Sports",
    iconName: "trophy",
    color: "3B82F6",
    bgColor: "0A1628",
  },
  brewery: {
    label: "Brewery",
    iconName: "beer",
    color: "16A34A",
    bgColor: "0A1A0E",
  },
  rooftop: {
    label: "Rooftop",
    iconName: "building2",
    color: "F97316",
    bgColor: "1A0E04",
  },
  lounge: {
    label: "Lounge",
    iconName: "sofa",
    color: "EC4899",
    bgColor: "1A0812",
  },
  wine_bar: {
    label: "Wine Bar",
    iconName: "bottleWine",
    color: "EC4899",
    bgColor: "1A0812",
  },
  speakeasy: {
    label: "Speakeasy",
    iconName: "lock",
    color: "EC4899",
    bgColor: "1A0812",
  },
  comedy_club: {
    label: "Comedy",
    iconName: "laugh",
    color: "EAB308",
    bgColor: "1A1404",
  },
  karaoke: {
    label: "Karaoke",
    iconName: "mic",
    color: "EAB308",
    bgColor: "1A1404",
  },
  distillery: {
    label: "Distillery",
    iconName: "flaskConical",
    color: "DC2626",
    bgColor: "1A0404",
  },
  hookah_lounge: {
    label: "Hookah",
    iconName: "wind",
    color: "0891B2",
    bgColor: "041418",
  },
  pool_hall: {
    label: "Pool / Billiards",
    iconName: "circle",
    color: "475569",
    bgColor: "0A0E14",
  },
  gay_bar: {
    label: "LGBTQ+",
    iconName: "heart",
    color: "EF4E2E",
    bgColor: "2A1208",
  },
  other: {
    label: "Other",
    iconName: "mapPin",
    color: "6B7280",
    bgColor: "0E0E0E",
  },
} as const;

type SharedVenueCategory = keyof typeof VENUE_CATEGORY_VISUALS;

export const VENUE_OWNER_CATEGORIES = [
  { value: "music_venue", label: "Music" },
  { value: "bar", label: "Bar" },
  { value: "dive_bar", label: "Dive Bar" },
  { value: "wine_bar", label: "Wine Bar" },
  { value: "speakeasy", label: "Speakeasy" },
  { value: "nightclub", label: "Club" },
  { value: "brewery", label: "Brewery" },
  { value: "distillery", label: "Distillery" },
  { value: "rooftop", label: "Rooftop" },
  { value: "sports_bar", label: "Sports" },
  { value: "lounge", label: "Lounge" },
  { value: "comedy_club", label: "Comedy" },
  { value: "karaoke", label: "Karaoke" },
  { value: "hookah_lounge", label: "Hookah" },
  { value: "pool_hall", label: "Pool / Billiards" },
  { value: "gay_bar", label: "LGBTQ+" },
  { value: "other", label: "Other" },
] as const;

export const VENUE_FILTER_CATEGORIES = [
  { value: "", label: "All" },
  { value: "music_venue", label: "Music" },
  { value: "bar", label: "Bars" },
  { value: "dive_bar", label: "Dive Bars" },
  { value: "wine_bar", label: "Wine Bars" },
  { value: "speakeasy", label: "Speakeasy" },
  { value: "nightclub", label: "Clubs" },
  { value: "brewery", label: "Brewery" },
  { value: "distillery", label: "Distillery" },
  { value: "rooftop", label: "Rooftop" },
  { value: "sports_bar", label: "Sports" },
  { value: "lounge", label: "Lounge" },
  { value: "comedy_club", label: "Comedy" },
  { value: "karaoke", label: "Karaoke" },
  { value: "hookah_lounge", label: "Hookah" },
  { value: "pool_hall", label: "Pool / Billiards" },
  { value: "gay_bar", label: "LGBTQ+" },
  { value: "other", label: "Other" },
] as const;

export function resolveSharedVenueCategory(
  category?: string | null,
): SharedVenueCategory | null {
  if (!category) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(VENUE_CATEGORY_VISUALS, category)
    ? (category as SharedVenueCategory)
    : null;
}

export function getSharedVenueCategoryVisual(
  category?: string | null,
): VenueCategoryVisualSpec {
  const resolvedCategory = resolveSharedVenueCategory(category);

  return resolvedCategory
    ? VENUE_CATEGORY_VISUALS[resolvedCategory]
    : VENUE_CATEGORY_VISUALS.other;
}

export function getSharedVenueMapCategoryVisual(
  category?: string | null,
): VenueCategoryVisualSpec {
  const resolvedCategory = resolveSharedVenueCategory(category);

  switch (resolvedCategory) {
    case "music_venue":
      return {
        label: "Music",
        iconName: "music",
        color: "EF4E2E",
        bgColor: "2A1208",
      };
    case "nightclub":
    case "lounge":
    case "speakeasy":
    case "gay_bar":
      return {
        label: "Nightlife",
        iconName: "music2",
        color: "A855F7",
        bgColor: "1A0A2A",
      };
    case "bar":
    case "dive_bar":
    case "wine_bar":
    case "brewery":
    case "distillery":
    case "hookah_lounge":
      return {
        label: "Drinks",
        iconName: "bottleWine",
        color: "3B82F6",
        bgColor: "0A1628",
      };
    case "comedy_club":
    case "karaoke":
      return {
        label: "Stage",
        iconName: "mic",
        color: "EAB308",
        bgColor: "1A1404",
      };
    case "sports_bar":
    case "pool_hall":
      return {
        label: "Games",
        iconName: "trophy",
        color: "EAB308",
        bgColor: "1A1404",
      };
    case "rooftop":
      return {
        label: "Rooftop",
        iconName: "building2",
        color: "F97316",
        bgColor: "1A0E04",
      };
    default:
      return VENUE_CATEGORY_VISUALS.other;
  }
}
